import { extendObservable, has as observableHas } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { FormGroup as BSFormGroup, HelpBlock as BSHelpBlock, ControlLabel, FormControl, InputGroup, } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { Descriptor } from "./Descriptor";
import { useFormContext } from "./Form";
import { useTranslation } from "./hooks/useTranslation";
import { useUtilityStyles } from "./hooks/useUtilityStyles";
import { classNames } from "./utils/classNames";
import { getIsOverMinFn, getIsUnderMaxFn, isEmail, isInteger, isNumber, isURL, } from "./utils/validators";
export const DEFAULT_ID = uuidv4();
export const ensureObservableValue = (obj, key) => {
    // Since key can be e.g. learners[0].firstName or learner.firstName
    // we want to make sure we're only extending the observable with the top-level key e.g. learners/learner
    const topLevelKey = key.split(/[[.]/)[0];
    if (!observableHas(obj, topLevelKey)) {
        extendObservable(obj, {
            [topLevelKey]: null,
        });
    }
    if (obj[topLevelKey] === undefined) {
        obj[topLevelKey] = null;
    }
};
let ERROR_ID_COUNTER = 0;
export function withFormWrapper({ WrappedComponent, options, }) {
    const LabeledInput = observer(({ valid, required, prepend, helpMessage, name, type, min, max, labelRemoved, "data-sentry-label": sentryLabel, ...props }) => {
        const { utilities } = useUtilityStyles(props?.styleOptions);
        const onComponentChange = props.onChange;
        if (name === undefined) {
            throw new Error("Input does not have a name");
        }
        const [errorId] = React.useState(() => `${ERROR_ID_COUNTER++}`);
        const [isRawValueInvalid, setisRawValueInvalid] = React.useState(false);
        const { values, errors, setIsDirty, isStatic } = useFormContext();
        const t = useTranslation();
        const isURLWithErrorMsg = (value) => valueIsBlank(value) || isURL(value) || t("Value must be a valid URL");
        const isEmailWithErrorMsg = (value) => valueIsBlank(value) ||
            isEmail(value) ||
            t("Value must be a valid email");
        const isNumberWithErrorMsg = (value) => valueIsBlank(value) ||
            isNumber(value) ||
            t("Value must be a valid number");
        const isIntegerWithErrorMsg = (value) => valueIsBlank(value) ||
            isInteger(value) ||
            t("Value must be a valid integer");
        const isOverMinWithErrorMsg = (minValue) => (value) => {
            const isOverMin = getIsOverMinFn(minValue);
            return (valueIsBlank(value) ||
                isOverMin(value) ||
                `${t("Value must be above minimum of")} ${minValue}`);
        };
        const isUnderMaxWithErrorMsg = (maxValue) => (value) => {
            const isUnderMax = getIsUnderMaxFn(maxValue);
            return (valueIsBlank(value) ||
                isUnderMax(value) ||
                `${t("Value must be below maximum of")} ${maxValue}`);
        };
        const isDateTimeWithErrorMsg = useCallback(() => {
            if (isRawValueInvalid) {
                if (type === "time") {
                    return t("Invalid Time");
                }
                if (type === "datetime") {
                    return t("Invalid Datetime");
                }
            }
            return true;
        }, [isRawValueInvalid, type]);
        React.useEffect(() => {
            ensureObservableValue(values, name);
            ensureObservableValue(errors, errorId);
        }, [values, errors, name, errorId]);
        const getRequiredValidator = React.useMemo(() => {
            return (value) => {
                if (typeof value === "object" &&
                    value !== null &&
                    options === "objectValidation") {
                    Object.keys(value).forEach((key) => {
                        if (valueIsBlank(value[key])) {
                            if (typeof required === "string") {
                                return required;
                            }
                            return t("Required");
                        }
                    });
                    return true;
                }
                if (valueIsBlank(value)) {
                    if (typeof required === "string") {
                        return required;
                    }
                    return t("Required");
                }
                return true;
            };
        }, [required]);
        const validators = React.useMemo(() => {
            const validFns = [];
            if (required)
                validFns.push(getRequiredValidator);
            if (valid)
                validFns.push(valid);
            switch (type) {
                case "email":
                    validFns.push(isEmailWithErrorMsg);
                    break;
                case "url":
                    validFns.push(isURLWithErrorMsg);
                    break;
                case "number":
                    validFns.push(isNumberWithErrorMsg);
                    break;
                case "integer":
                    validFns.push(isIntegerWithErrorMsg);
                    break;
                case "time":
                    validFns.push(isDateTimeWithErrorMsg);
                    break;
                case "datetime":
                    validFns.push(isDateTimeWithErrorMsg);
                    break;
            }
            if (min)
                validFns.push(isOverMinWithErrorMsg(min));
            if (max)
                validFns.push(isUnderMaxWithErrorMsg(max));
            return validFns;
        }, [type, required, valid, min, max, isDateTimeWithErrorMsg]);
        const handleInputStatusChange = useCallback((isInvalid) => {
            setisRawValueInvalid(isInvalid);
        }, []);
        // this should not be needed, but the typing is being problematic
        const Component = WrappedComponent;
        const { validationState, errorMessage } = validateInput(values.get(name), validators, options === "objectValidation");
        React.useEffect(() => {
            if (errors[errorId] !== validationState) {
                errors[errorId] = validationState;
            }
        }, [errors, errorId, validationState]);
        // clear error state when input is unmounted
        const [onUnmount] = React.useState(() => () => {
            if (errors[errorId] !== null) {
                errors[errorId] = null;
            }
        });
        React.useEffect(() => onUnmount, [onUnmount]);
        const onChange = (newValue, oldValue, newLabel, oldLabel) => {
            setIsDirty(true);
            if (onComponentChange) {
                onComponentChange(newValue, oldValue, newLabel, oldLabel);
            }
        };
        const inputType = type === "integer" ? "number" : type;
        if ((!props.forceAlwaysLive && isStatic) || options === "validationOnly")
            return (React.createElement(Component, { name: name, type: inputType, min: min, max: max, ...props }));
        if (prepend) {
            return (React.createElement(HorizontalFormGroup, { uniqueId: props.uniqueId || DEFAULT_ID, label: props.label, name: name, prepend: prepend, validationState: validationState, dataId: props.dataId, dataLabel: props.dataLabel, dataTestId: props.dataTestId, sentryLabel: sentryLabel, className: utilities },
                React.createElement(Component, { name: name, type: inputType, min: min, max: max, ...props, onChange: onChange, onInputStatusChange: handleInputStatusChange })));
        }
        return (React.createElement(FormGroup, { className: utilities, validationState: validationState, name: name, labelHidden: props.labelHidden, options: options, uniqueId: props.uniqueId ? `${props.uniqueId}` : DEFAULT_ID, describingId: props?.describingId, dataId: props.dataId, dataLabel: props.dataLabel, dataTestId: props.dataTestId, sentryLabel: sentryLabel },
            React.createElement("div", { className: classNames(helpMessage
                    ? "form-group-top-wrapper--with-helpmessage"
                    : "form-group-top-wrapper", { "label-hidden": !!props.labelHidden }) },
                (options === "group" || options === "omitHTMLLabel") && (React.createElement("span", { className: "form-control-static-label", id: `${name}-title-${props.uniqueId || DEFAULT_ID}` }, props.label)),
                !labelRemoved &&
                    options !== "omitHTMLLabel" &&
                    options !== "group" &&
                    props.label ? (React.createElement(ControlLabel, { id: props?.describingId }, props.label)) : null,
                options !== "checkbox" && errorMessage ? (React.createElement(BSHelpBlock, { className: "pull-right", style: { margin: 0 }, "data-hook": "required-warning" }, errorMessage)) : null,
                helpMessage && (React.createElement("div", { className: "pull-right helpmessage-wrapper" },
                    React.createElement(Descriptor, { message: helpMessage, placement: "top" })))),
            React.createElement("div", { style: !labelRemoved ? { position: "relative" } : {} },
                React.createElement(Component, { name: name, type: inputType, min: min, max: max, ...props, onChange: onChange, onInputStatusChange: handleInputStatusChange }),
                React.createElement(FormControl.Feedback, null),
                options === "checkbox" && (React.createElement(BSHelpBlock, { style: { fontSize: 12 }, "data-hook": "required-warning" }, errorMessage)))));
    });
    return LabeledInput;
}
const FormGroup = ({ children, validationState, name, options, labelHidden, uniqueId, dataId, dataLabel, dataTestId, className, describingId, sentryLabel, }) => {
    const controlId = describingId === undefined ? `form-group-${name}-${uniqueId}` : undefined;
    if (options === "group") {
        return (React.createElement(BSFormGroup, { validationState: validationState === "error" ? "warning" : null, controlId: controlId, role: "group", "aria-labelledby": `${name}-title-${uniqueId}`, className: classNames(labelHidden ? "label-hidden" : "", className), "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId, "data-sentry-label": sentryLabel }, children));
    }
    return (React.createElement(BSFormGroup, { validationState: validationState === "error" ? "warning" : null, controlId: controlId, className: classNames(labelHidden ? "label-hidden" : "", className), "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId, "data-sentry-label": sentryLabel }, children));
};
const HorizontalFormGroup = ({ children, uniqueId, label, prepend, name, validationState, dataId, dataLabel, dataTestId, className, sentryLabel, }) => {
    return (React.createElement(BSFormGroup, { validationState: validationState === "error" ? "warning" : null, className: classNames("horizontal-form-group", className), "data-id": dataId, "data-label": dataLabel, "data-testid": dataTestId, "data-sentry-label": sentryLabel },
        React.createElement(InputGroup, null,
            prepend && (React.createElement("label", { className: "input-group-addon", htmlFor: `form-group-${name}-${uniqueId}` }, label)),
            children,
            React.createElement(FormControl.Feedback, null))));
};
function validateInput(value, validators, objectValidation) {
    let errorMessage;
    let validationState = null;
    if (typeof value === "object" && value !== null && objectValidation) {
        Object.keys(value).forEach((key) => {
            for (let validator of validators) {
                const validationResult = validator(value[key]);
                if (validationResult !== true) {
                    validationState = "error";
                    if (typeof validationResult === "string") {
                        errorMessage = validationResult;
                    }
                }
            }
        });
    }
    for (let validator of validators) {
        const validationResult = validator(value);
        if (validationResult !== true) {
            validationState = "error";
            if (typeof validationResult === "string") {
                errorMessage = validationResult;
            }
            return { validationState, errorMessage };
        }
    }
    return { validationState, errorMessage };
}
function valueIsBlank(value) {
    return (value === undefined ||
        value === null ||
        value === "" ||
        value === false ||
        value.length === 0);
}
export const emptyStringIfNull = (value) => {
    return value === null || value === undefined ? "" : value;
};
export const HelpBlock = (props) => {
    const classes = useUtilityStyles(props?.styleOptions);
    return (React.createElement(BSHelpBlock, { className: classNames("pull-right", classes.utilities) }, props.message));
};
